import React, { FC, useEffect, useState } from 'react';
import { View, useWindowDimensions, ScrollView, Pressable, KeyboardAvoidingView, Platform } from 'react-native'
import { useIsFocused } from '@react-navigation/native';
import { useFormik } from 'formik';
import * as yup from 'yup'
import Toast from 'react-native-toast-message';
import Popover from 'react-native-popover-view/dist/Popover';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../../redux/hooks';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Common Components
import CommonHeader from '../../../shared/commonComponents/commonHeader';
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox';
import { DisplaySettingsStyle } from '../../../themes/components/settings/displaySettingsStyle';
import { appColors, appFonts, fontSize } from '../../../themes/appTheme';
import { config } from '../../../shared/environment';
import { getDisplayData, updateDisplay, setScreenMode } from '../../../redux/slices/displaySettingSlice';
import { screenModeType, timeFormatType, tomeFormatDropdown, diaplayValue, updatePayload } from './displaySettigModule';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';
import { ZenButton, ZenDivider, ZenDropdown, ZenIcon, ZenInput, ZenLoader, ZenRadioButton, ZenSwitch, ZenText, ZenToolTip } from 'zen-core-elements';
import { getDisplaySetting } from '../../../redux/slices/commonSlice';
import { ThemeConsumer } from '@rneui/themed';
import { shallowEqual } from 'react-redux';

const DisplaySettings: FC = () => {
    const { isWrite: isSetttingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual)
    // Variable used to store Focused state
    const isFocused = useIsFocused()
    // Variable which is used to navigation
    const navigation = useAppNavigation()
    // Variable which is used to render styles based on device
    const { width } = useWindowDimensions()
    // Variable which is used to handle loader
    const [load, setLoad] = useState<boolean>(false)
    // Variable which is used to store display data
    const [displayData, setDisplayData] = useState<diaplayValue>()
    // Variable which is used to handle device mode
    const mode: screenModeType[] = [{ id: 1, screenMode: 'Dark', code: "DARK", value: "dark", label: "dark" }, { id: 2, screenMode: 'Light', code: "LIGHT", value: "light", label: "light" }]
    // Variable which is used to store checkbox value 
    const selectTime: timeFormatType[] = [{ id: 1, value: "1 minute", label: "1 minute", name: '1 minute', code: 1 }, { id: 2, value: "2 minutes", label: "2 minutes", name: '2 minutes', code: 2 }, { id: 3, value: "5 minutes", label: "5 minutes", name: '5 minutes', code: 5 }, { id: 4, value: "10 minutes", label: "10 minutes", name: '10 minutes', code: 10 }, { id: 5, value: "Custom", label: "Custom", name: 'Custom', code: "CUSTOM" }]
    // Variable which is used to store dropdown value 
    const timeFormat: tomeFormatDropdown[] | any = ([{ id: 1, value: "Minutes", label: 'Minutes', code: "MINUTES" }, { id: 2, value: "Hours", label: 'Hours', code: "HOURS" }])
    // Variable which is used to store leave page state
    const [leavePopover, setLeavePopover] = useState<boolean>(false)
    // Variable which is used to close/open the dropdown
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
    // Redux dispatch
    const dispatch = useAppDispatch()

    //State variable used to store leave page dialogbox props
    const leaveProps = {
        content: "You have unsaved changes. Are you sure you want to leave this page?",
        button: [{ id: 1, name: 'No', color: appColors.primary, backgroundColor: appColors.light }, { id: 2, name: 'Yes', backgroundColor: appColors.primary, color: appColors.light }],
        icon: { type: 'material-community', name: 'run', color: appColors.primary }
    }

    // useEffect hook which is used to call getDisplaySettings function when page is focused
    useEffect(() => {
        if (isFocused) {
            setLoad(true)
            getDisplaySettings()
        }
    }, [isFocused])

    // Method which is used to get displaySettings
    const getDisplaySettings = async () => {
        await dispatch(getDisplayData({ storeId: config?.storeId })).unwrap().then((res) => {
            if (res) {
                setDisplayData(res?.displayData)
                setLoad(false)
            }
        }).catch((Err) => {
            console.log("Error in get display data", Err)
        })
    }

    const validationSchema = yup.object().shape({
        timeValue: yup.string().label("timeValue"),
        time: yup
            .number()
            .typeError("Time must be a valid number*")
            .when("timeValue", {
                is: (val: string) => val?.toUpperCase() === "CUSTOM",
                then: (schema) => schema.required("Time is required*").typeError("Time must be a valid number*"),
                otherwise: (schema) => schema.notRequired(),
            }),
    });

    const isCustomOrNot = displayData?.isCustom ? "Custom" : selectTime?.find((item: timeFormatType) => item?.code == displayData?.timeValue)?.value;


    // Variable which is used to access formik values
    const formik = useFormik({
        initialValues: {
            isUseMyDeviceSettings: displayData?.isUseMyDeviceSettings ? displayData?.isUseMyDeviceSettings : false,
            isLockScreenInactive: displayData?.isLockScreenInactive ? displayData?.isLockScreenInactive : false,
            isLockScreenAfterSale: displayData?.isLockScreenAfterSale ? displayData?.isLockScreenAfterSale : false,
            isUseCameraToScanBarcode: displayData?.isUseCameraToScanBarcode ? displayData?.isUseCameraToScanBarcode : false,
            screenMode: displayData?.screenMode ? mode?.find((item: screenModeType) => item?.code == displayData?.screenMode)?.value : "light",
            timeValue: displayData ? isCustomOrNot : selectTime?.find((item: timeFormatType) => item.code == 1)?.value,
            time: displayData?.isCustom ? displayData?.timeValue?.toString() : "",
            timeUnit: displayData ? timeFormat?.find((item: tomeFormatDropdown) => item?.code == displayData?.timeUnit)?.value : timeFormat?.find((item: tomeFormatDropdown) => item?.code == "MINUTES")?.value
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            updateDisplaySetting(values)
            setLoad(true)
        }
    })
    // Method which is used to update display settings
    const updateDisplaySetting = async (value: updatePayload) => {
        let data = {
            isUseMyDeviceSettings: value?.isUseMyDeviceSettings,
            screenMode: value?.screenMode ? mode?.find((item: screenModeType) => item?.value == value?.screenMode)?.code : "",
            isLockScreenInactive: value?.isLockScreenInactive,
            timeValue: value?.timeValue == "Custom" ? value?.time.toString() : value?.timeValue != undefined ? selectTime?.find((item: timeFormatType) => item?.value == value?.timeValue)?.code.toString() : "",
            timeUnit: value?.timeValue == "Custom" ? timeFormat?.find((item: tomeFormatDropdown) => item?.value == value?.timeUnit)?.code : "MINUTES",
            isCustom: value?.timeValue == "Custom" ? true : false,
            isLockScreenAfterSale: value?.isLockScreenAfterSale,
            isUseCameraToScanBarcode: value?.isUseCameraToScanBarcode
        }
        await dispatch(updateDisplay({ storeId: config?.storeId, data: data })).unwrap().then(async (updateRes) => {
            if (updateRes) {
                let screenMode: string | undefined = mode?.find((item: screenModeType) => item?.value == value?.screenMode)?.code
                if (screenMode) {
                    try {
                        await AsyncStorage.setItem("screenMode", screenMode);
                        dispatch(setScreenMode(screenMode));
                    } catch (error) {
                        console.error("Error saving screenMode:", error);
                    }
                }
                setLoad(true)
                await dispatch(getDisplaySetting({ storeId: config.storeId })).unwrap()
                navigation.goBack()
                Toast?.show({ type: "success", text1: "Success", text2: "Display settigs were updated successfully" })
            }
        }).catch((Err) => {
            console.log("Error in update display setting", Err)
            Toast?.show({ type: "error", text1: "Error", text2: "Error in update display settings." });
        })
    }

    // Function which is used for leave confirmation message
    const confirmation = (value: { name: string }) => {
        if (value?.name == "Yes") {
            setLeavePopover(false)
            navigation.goBack()
        }
        else {
            setLeavePopover(false)
        }
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} enabled style={{ flex: 1 }}>
                    <CommonHeader headerName='Display Customization' backIcon={true} backIconOnpress={() => { formik?.dirty ? setLeavePopover(true) : navigation.goBack() }} />
                    <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignSelf: 'flex-end', gap: 10, margin: 10 }}>
                            {/* <ZenButton title='Cancel'
                                onPress={() => { formik?.dirty ? setLeavePopover(true) : navigation.goBack() }}
                                type='outline'
                                outlineColor={theme?.primary}
                                titleStyle={{ fontSize: fontSize.tinyFontSize, color: theme?.primary }}
                                buttonStyle={{
                                    backgroundColor: theme?.light,
                                    borderRadius: 10,
                                    paddingHorizontal: 15,
                                }}
                                containerStyle={{ borderRadius: 10, }}
                            /> */}
                            {isSetttingWrite && <ZenButton
                                title='Save'
                                disabled={!formik?.dirty || !formik?.isValid}
                                onPress={() => { formik?.handleSubmit() }}
                                disabledStyle={{ borderColor: 'transparent' }}
                                titleStyle={{ fontSize: fontSize.tinyFontSize, color: theme?.light }}
                                buttonStyle={{
                                    backgroundColor: theme?.primary,
                                    borderRadius: 10,
                                    paddingHorizontal: 15,
                                    borderColor: 'transparent',
                                    borderWidth: 1
                                }}
                                containerStyle={{ borderRadius: 10, }}
                            />}
                        </View>

                        {load ?
                            < View style={{ flex: 1, alignItems: "center", justifyContent: "center", marginVertical: 240 }}>
                                <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                            </View> :
                            <ScrollView showsVerticalScrollIndicator={false}>
                                <View style={{ flex: 1, gap: 10, marginBottom: 10 }}>
                                    {/* <View style={{ paddingHorizontal: 15, marginTop: 20 }}>
                                        <View style={{ backgroundColor: appColors.light, borderRadius: 14, paddingHorizontal: 18, paddingVertical: 20, }}>
                                            <ZenText content='Screen mode' size={fontSize?.largerFontSize} bold={true} color={appColors.primary} style={{ marginBottom: 10 }} />
                                            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", }} >
                                                <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
                                                    <ZenText content="Use my device settings" size={fontSize?.largerFontSize} bold={true} color={appColors.lightDark} />
                                                    <ZenToolTip height={90} width={230} backgroundColor={appColors.red} popoverStyle={{ elevation: 6 }}
                                                        content={
                                                            <ZenText content="Automatically adjusts screen mode to match your device theme." size={fontSize.smallFontSize} color={appColors.dark} />
                                                        }
                                                        popover={
                                                            <Pressable>
                                                                <ZenIcon name="information" type="material-community" size={18} color={appColors.primary} style={{ marginLeft: 8 }} />
                                                            </Pressable>
                                                        }
                                                    />
                                                </View>
                                                <ZenSwitch
                                                    value={formik?.values?.isUseMyDeviceSettings}
                                                    disabled={!isSetttingWrite}
                                                    onValueChange={(value) => { formik.setFieldValue("isUseMyDeviceSettings", value); }}
                                                    style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }], }}
                                                    activeColor={appColors.primary}
                                                    activeThumbColor={appColors.light}
                                                    inActiveColor={appColors.borderColor}
                                                    inactiveThumbColor={appColors.light}
                                                />
                                            </View>

                                            {!formik?.values?.isUseMyDeviceSettings && (
                                                <ZenDivider style={{
                                                    height: 1,
                                                    backgroundColor: appColors.borderColor,
                                                    marginVertical: 10,
                                                    opacity: 0.5,
                                                }} />)}

                                            {!formik?.values?.isUseMyDeviceSettings && (
                                                <View>
                                                    <ZenText content="Select mode" size={fontSize?.largerFontSize} bold={true} color={appColors.lightDark} />
                                                    <View
                                                        style={{ gap: 12, paddingLeft: 5, }}>
                                                        <ZenRadioButton
                                                            disabled={!isSetttingWrite}
                                                            initialSelected={formik?.values?.screenMode!!}
                                                            options={mode.map(opt => ({ ...opt }))}
                                                            titleStyle={{ color: appColors.lightDark, fontSize: fontSize?.largerFontSize, }}
                                                            containerStyle={{ flexDirection: "column", gap: 12, alignItems: "flex-start", }}
                                                            onSelect={(value) => {
                                                                formik.setFieldValue("screenMode", value);
                                                            }}
                                                        />
                                                    </View>
                                                </View>
                                            )}
                                        </View>
                                    </View> */}
                                    <View style={{ borderRadius: 10, backgroundColor: appColors?.light, marginHorizontal: 15, marginTop: 15, padding: 15 }}>
                                        <ZenText content='Lock Screen' size={fontSize?.largerFontSize} bold={true} color={appColors.primary} style={{ marginBottom: 10 }} />
                                        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", }} >
                                            <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
                                                <ZenText content="Lock screen when inactive" size={fontSize?.largerFontSize} bold={true} color={appColors.lightDark} />
                                                <ZenToolTip
                                                    height={60}
                                                    width={230}
                                                    backgroundColor={appColors.lightBackgroundColor}
                                                    popoverStyle={{ elevation: 6 }}
                                                    content={
                                                        <ZenText content="Staff have to enter the security PIN to unlock the screen" size={fontSize.smallFontSize} color={appColors.dark} />
                                                    }
                                                    popover={
                                                        <Pressable>
                                                            <ZenIcon name="information" type="material-community" size={18} color={appColors.primary} style={{ marginLeft: 8 }} />
                                                        </Pressable>
                                                    }
                                                />
                                            </View>
                                            <ZenSwitch
                                                disabled={!isSetttingWrite}
                                                onValueChange={(value) => {
                                                    if (value) {
                                                        formik?.setFieldValue("isLockScreenInactive", value)
                                                    } else {
                                                        formik?.setFieldValue("isLockScreenInactive", value)
                                                        formik?.setFieldValue("timeValue", selectTime?.find((item: timeFormatType) => item.code == 1)?.value)
                                                        formik?.setFieldValue("time", "")
                                                    }
                                                    formik?.setFieldTouched("time", false)
                                                }}
                                                value={formik?.values?.isLockScreenInactive}
                                                style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }], }}
                                                activeColor={appColors?.primary}
                                                activeThumbColor={appColors?.light}
                                                inActiveColor={appColors.borderColor}
                                                inactiveThumbColor={appColors?.light} />
                                        </View>
                                        <View style={[DisplaySettingsStyle.viewStyle, { flexDirection: width > 600 ? 'row' : undefined }]}>
                                            {formik?.values?.isLockScreenInactive &&
                                                <ZenRadioButton
                                                    disabled={!isSetttingWrite}
                                                    initialSelected={formik?.values?.timeValue!!}
                                                    options={selectTime.map(opt => ({ ...opt }))}
                                                    titleStyle={{ color: appColors.lightDark, fontSize: fontSize?.largerFontSize, }}
                                                    containerStyle={{ flexDirection: "column", flex: 1 }}
                                                    onSelect={(value) => {
                                                        formik?.setFieldValue("timeValue", value);
                                                    }}
                                                />
                                            }

                                        </View>
                                        {(formik?.values?.timeValue == 'Custom' && formik?.values?.isLockScreenInactive) &&
                                            <View style={{ flex: 1, flexDirection: "row", gap: 20 }}>
                                                <View style={{ flex: 0.5 }} >
                                                    <ZenInput
                                                        label='Time *'
                                                        disabled={!isSetttingWrite}
                                                        onChangeText={(text: string) => { formik.setFieldValue('time', text); formik?.setFieldTouched("time", true) }}
                                                        value={String(formik?.values?.time)}
                                                        labelStyle={DisplaySettingsStyle.labelStyle}
                                                        keyboardType='numeric'
                                                        inputStyle={{ fontSize: fontSize?.tinyFontSize, color: appColors?.dark, fontFamily: appFonts?.medium }}
                                                        inputContainerStyle={{ paddingHorizontal: 8, height: 45 }}
                                                        type='outlined'
                                                        onBlur={formik.handleBlur('time')}
                                                        errorMessage={formik?.touched?.time && formik?.values?.time == "" ? "Time is required" : undefined}
                                                        errorStyle={DisplaySettingsStyle.errorStyle}
                                                    />
                                                </View>

                                                <View style={{ flex: 0.5 }} >
                                                    <ZenDropdown
                                                        dropdownDirection='TOP'
                                                        title='Time Format'
                                                        multiple={false}
                                                        disabled={!isSetttingWrite}
                                                        setValue={() => { }}
                                                        open={dropdownOpen}
                                                        setOpen={setDropdownOpen}
                                                        items={timeFormat}
                                                        value={formik?.values?.timeUnit}
                                                        onPress={() => { formik?.setFieldTouched("timeUnit", true) }}
                                                        onSelectItem={(type) => { formik?.setFieldValue('timeUnit', type?.value) }}
                                                        maxHeight={80}
                                                        style={{ height: 46 }}
                                                        titleStyle={{ fontFamily: theme?.medium, fontSize: fontSize.tinyFontSize, color: theme?.grey }}
                                                        labelStyle={DisplaySettingsStyle.dropDownlabelStyle}
                                                        selectedItemLabelStyle={{ fontSize: fontSize.tinyFontSize, fontFamily: theme?.medium, }}

                                                    />
                                                </View>

                                            </View>
                                        }
                                        <View style={[DisplaySettingsStyle.cardView, { marginTop: (formik?.values?.timeValue == 'Custom') ? 0 : 10 }]}>
                                            <ZenText content='Lock screen after sale' color={appColors.lightDark} size={fontSize?.mediumFontSize} bold={true} style={{ marginTop: 5 }} />
                                            <ZenSwitch
                                                disabled={!isSetttingWrite}
                                                onValueChange={(value) => { formik?.setFieldValue("isLockScreenAfterSale", value) }}
                                                value={formik?.values?.isLockScreenAfterSale}
                                                style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }], }}
                                                activeColor={appColors?.primary}
                                                activeThumbColor={appColors?.light}
                                                inActiveColor={appColors.borderColor}
                                                inactiveThumbColor={appColors?.light} />
                                        </View>
                                    </View>
                                    <View style={{ borderRadius: 10, backgroundColor: appColors?.light, marginHorizontal: 15, marginTop: 15, padding: 15 }}>
                                        <ZenText content='Barcode' size={fontSize?.largerFontSize} bold={true} color={appColors.primary} />
                                        <View style={DisplaySettingsStyle.cardView}>
                                            <ZenText content='Use camera to scan barcode' color={appColors.lightDark} size={fontSize?.mediumFontSize} bold={true} style={{ marginTop: 10 }} />
                                            <ZenSwitch
                                                disabled={!isSetttingWrite}
                                                onValueChange={(value) => { formik?.setFieldValue("isUseCameraToScanBarcode", value) }}
                                                value={formik?.values?.isUseCameraToScanBarcode}
                                                style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }], }}
                                                activeColor={appColors?.primary}
                                                activeThumbColor={appColors?.light}
                                                inActiveColor={appColors.borderColor}
                                                inactiveThumbColor={appColors?.light} />
                                        </View>
                                    </View>
                                </View>
                            </ScrollView>
                        }
                        <Popover isVisible={leavePopover} popoverStyle={{ width: width > 900 ? width / 2 : width > 600 ? 450 : 350, borderRadius: 10 }}>
                            <CommonDialogBox type="OTHER" dialog={leaveProps} confirmation={confirmation} />
                        </Popover>
                    </View >
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>

    )
}

export default DisplaySettings;